// src/components/common/CartDrawer.jsx
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, ShoppingBag, Minus, Plus } from "lucide-react";

function FontImport() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;1,9..144,500&family=DM+Sans:wght@400;500;600;700&display=swap');
      .og-display{font-family:'Fraunces',serif}
      .og-body{font-family:'DM Sans',sans-serif}
    `}</style>
  );
}

const UPSELL = [
  { id:"u1", name:"DIY Bag Charm Kit",       subtitle:"Accessories", price:1499, comparePrice:1899, image:"https://oddgiraffe.com/cdn/shop/files/Asset_16_b63c8344-4654-43eb-b673-94cd6f8dd939.jpg?v=1762997463&width=400" },
  { id:"u2", name:"Plan It All | Sticker Set",subtitle:"Accessories", price:399,  comparePrice:599,  image:"https://oddgiraffe.com/cdn/shop/files/7-stickers_414214d8-d3da-473b-affa-dc04d9af35e3.webp?v=1695663625&width=400" },
  { id:"u3", name:"Daisy Pen Clip",           subtitle:"Accessories", price:399,  comparePrice:499,  image:"https://oddgiraffe.com/cdn/shop/files/Asset14.webp?v=1733332617&width=400" },
  { id:"u4", name:"Good Times | Stickers",    subtitle:"Accessories", price:249,  comparePrice:349,  image:"https://oddgiraffe.com/cdn/shop/files/stickers--2.webp?v=1695663844&width=400" },
];

export default function CartDrawer({ isOpen, onClose, cartItems = [], onRemove, updateQty }) {
  const navigate = useNavigate();
  const drawerRef = useRef();

  useEffect(() => {
    function handleClick(e) {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) onClose();
    }
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * (item.qty || 1), 0);
  const freeShippingThreshold = 2000;
  const progress = Math.min((subtotal / freeShippingThreshold) * 100, 100);
  const remaining = freeShippingThreshold - subtotal;

  return (
    <>
      <FontImport/>

      {/* Overlay */}
      <div className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}/>

      {/* Drawer */}
      <div ref={drawerRef}
        className={`fixed right-0 top-0 z-50 h-full w-full max-w-[400px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out og-body ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18}/>
            <span className="font-bold text-sm">Your Bag ({cartItems.length} {cartItems.length===1?"item":"items"})</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors">
            <X size={18}/>
          </button>
        </div>

        {/* Scrollable content */}
    <div className="flex-1 overflow-y-auto min-h-0" data-lenis-prevent>
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 px-8 text-center">
              <ShoppingBag size={48} className="text-gray-200"/>
              <p className="og-display text-xl text-gray-400 italic">Your bag is empty</p>
              <button onClick={onClose}
                className="mt-2 rounded-full bg-[#1A1A1A] px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90">
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              {/* Made to Order banner */}
              <div className="mx-4 mt-4 rounded-lg bg-[#F5F5F0] p-4">
                <p className="text-sm font-bold text-[#1A1A1A]">Our Products Are Made To Order</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  At Aishcraft, we print on demand — less waste and designs that are truly one of a kind.
                </p>
              </div>

              {/* Free shipping progress */}
              <div className="px-4 mt-4">
                <p className="text-xs font-semibold text-[#1A1A1A] mb-1.5">
                  {subtotal >= freeShippingThreshold
                    ? "🎉 You are eligible for free shipping!"
                    : `Add Rs. ${remaining.toLocaleString("en-IN")} more for free shipping`}
                </p>
                <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full rounded-full bg-[#FFEB3B] transition-all duration-500" style={{ width:`${progress}%` }}/>
                </div>
              </div>

              {/* Cart items */}
              <div className="px-4 mt-5 flex flex-col gap-4">
                {cartItems.map((item, i) => (
                  <div key={item.id || i} className="flex gap-3 pb-4 border-b border-gray-100 last:border-0">
                    <div className="w-20 h-24 rounded-md overflow-hidden bg-[#EFE9DD] flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold leading-snug">{item.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{item.subtitle}</p>
                      {item.color && <p className="text-xs text-gray-400 mt-0.5">Color: {item.color}</p>}
                      {item.paperType && <p className="text-xs text-gray-400 mt-0.5">Paper: {item.paperType}</p>}
                      {item.personalisedName && <p className="text-xs text-gray-400 mt-0.5">Name: {item.personalisedName}</p>}

                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm font-bold">Rs. {item.price.toLocaleString("en-IN")}</span>
                        {item.comparePrice && (
                          <span className="text-xs text-gray-400 line-through bg-[#FFEB3B]/60 px-1 rounded">
                            Rs. {item.comparePrice.toLocaleString("en-IN")}
                          </span>
                        )}
                      </div>

                      {/* Qty controls */}
                      <div className="flex items-center gap-2 mt-2 border border-gray-200 rounded-full w-fit px-2 py-0.5">
                        <button onClick={() => updateQty && updateQty(i, Math.max(1,(item.qty||1)-1))}
                          className="w-5 h-5 flex items-center justify-center hover:bg-gray-100 rounded-full">
                          <Minus size={10}/>
                        </button>
                        <span className="text-xs font-semibold w-4 text-center">{item.qty||1}</span>
                        <button onClick={() => updateQty && updateQty(i, (item.qty||1)+1)}
                          className="w-5 h-5 flex items-center justify-center hover:bg-gray-100 rounded-full">
                          <Plus size={10}/>
                        </button>
                      </div>

                     <button
  onClick={() => onRemove && onRemove(i)}
  className="text-xs text-gray-400 mt-2 underline underline-offset-2 hover:text-red-500 transition-colors"
>
  Remove
</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* You may also like */}
              {/* <div className="px-4 mt-6">
                <p className="text-sm font-bold mb-3">You may also like</p>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {UPSELL.map((uitem) => (
                    <div key={uitem.id} className="flex-shrink-0 w-32">
                      <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F0E8] mb-2">
                        <img src={uitem.image} alt={uitem.name} className="w-full h-full object-cover"/>
                      </div>
                      <p className="text-[11px] font-bold leading-tight line-clamp-2">{uitem.name}</p>
                      <p className="text-[10px] text-gray-400">{uitem.subtitle}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-xs font-bold">Rs. {uitem.price}</span>
                        <span className="text-[10px] text-gray-400 line-through">Rs. {uitem.comparePrice}</span>
                      </div>
                      <button className="mt-1.5 w-full rounded border border-[#1A1A1A] text-[11px] font-semibold py-1 hover:bg-[#1A1A1A] hover:text-white transition-colors">
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              </div> */}

              {/* Discount code */}
              <div className="px-4 mt-20">
                <p className="text-xs font-semibold text-gray-500 mb-2">Apply Discount Code at Checkout</p>
              </div>

              {/* Subtotal */}
              <div className="px-4 mt-3 pt-3 border-t border-gray-100">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Subtotal (tax included)</span>
                  <span className="font-bold">Rs. {subtotal.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Shipping</span>
                  <span className="text-gray-400">calculated in checkout</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer CTA */}
        {cartItems.length > 0 && (
          <div className="px-4 py-4 border-t border-gray-100 bg-white flex-shrink-0">
            <div className="flex justify-between items-center mb-3">
              <span className="font-bold text-base">Subtotal:</span>
              <span className="font-bold text-base">Rs. {subtotal.toLocaleString("en-IN")}</span>
            </div>
            <button onClick={() => { onClose(); navigate("/checkout"); }}
              className="w-full rounded-xl bg-[#1A1A1A] py-4 text-sm font-bold text-white hover:opacity-90 transition-opacity">
              Proceed to checkout
            </button>
          </div>
        )}
      </div>
    </>
  );
}